import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { RichTextEditor } from "components/DesignSystem/RichTextEditor/RichTextEditor";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useCreateOrUpdateInvoiceSettingsMutation } from "store/apis/invoices";
import { InvoiceSettingsType } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import {
  INVOICE_FOOTER_TEMPLATE,
  TEMPLATE_HELPER_TEXT,
} from "constants/invoicing";

type InvoiceFooterModalProps = {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  invoiceSetting: InvoiceSettingsType;
};

type InvoiceFooterValues = {
  invoice_footer: string;
};

export const InvoiceFooterModal = ({
  isOpen,
  close,
  entityId,
  invoiceSetting,
}: InvoiceFooterModalProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const [createInvoiceSetting, { isLoading }] =
    useCreateOrUpdateInvoiceSettingsMutation();

  const initialValues = {
    invoice_footer: invoiceSetting.invoice_footer || INVOICE_FOOTER_TEMPLATE,
  };

  const handleSubmit = async (values: InvoiceFooterValues) => {
    const { address, ...settings } = invoiceSetting;

    try {
      await createInvoiceSetting({
        groupId,
        entityId,
        payload: { ...settings, invoice_footer: values.invoice_footer },
      }).unwrap();
      successToast({ message: "Invoice footer enabled" });
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="regular">
        <FormikForm<InvoiceFooterValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <Modal.Header>
            <Modal.Title>Invoice Footer</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-flex t-flex-col t-gap-4 sm:t-px-4">
            <p className="t-m-0 t-text-body t-text-text-30">
              Once enabled, footer will be added by default for all invoices
              generated
            </p>
            <div className="t-flex t-flex-col t-gap-1">
              <RichTextEditor name="invoice_footer" label="Enter footer text" />
              <span className="t-text-body-sm-italic t-italic t-text-text-30">
                {TEMPLATE_HELPER_TEXT}
              </span>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button">Cancel</Button>
            </Modal.RawClose>
            <Button
              type="submit"
              customType="primary"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Save footer
            </Button>
          </Modal.FooterButtonGroup>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
